// src/components/inventory/InventoryFormModal.js
//
// --- UPDATE (Inventory MOW+ v2) ----------------------------------------------------------
//   • FlowSense AI panel: for an existing item the ML insight (usage, re-order point, safety
//     stock, EOQ, days of cover, risk, links) with hints when the manual alert threshold or
//     re-order quantity disagree with real usage — "Use FlowSense values" copies them in. New
//     items get checks too (price 0, threshold 0).
//   • Live: if someone else changes this item while the form is open (WebSocket), a banner shows
//     the new quantity so nobody saves over it unknowingly.
//   • Fixed: the AI icon inside the name field was empty (the local IconButton ignored `icon`);
//     "subtle" is not a Chakra Button variant; save errors now show the server message
//     (e.g. duplicate SKU); Supplier is only required when suppliers exist.
// --- END UPDATE ---------------------------------------------------------------------------
import React, { useState, useEffect } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton,
  Button, FormControl, FormLabel, Input, Textarea, NumberInput, NumberInputField,
  VStack, HStack, useToast, Divider, Flex, useColorModeValue, Select, Icon, SimpleGrid,
  Box, Text, Tooltip, InputGroup, InputRightElement, Spinner
} from '@chakra-ui/react';
import { Alert, AlertIcon, Badge, Skeleton, FormHelperText } from '@chakra-ui/react'; // --- UPDATE
import { FaRobot } from 'react-icons/fa';
import { MdInventory, MdAutoAwesome } from 'react-icons/md';
import { MdCheckCircle } from 'react-icons/md'; // --- UPDATE
import inventoryService from '../../services/inventoryService';
import supplierService from '../../services/supplierService';
import inventoryCategoryService from '../../services/inventoryCategoryService';
// --- UPDATE (MOW+ v2)
import inventoryIntelApi from '../../services/inventoryIntelApi';
import useInventoryLive from '../../hooks/useInventoryLive';
import { itemFormHints, explainScreen, RISK_META, money, dash } from './inventoryAiIntel';
import FlowSenseInsightBar from './FlowSenseInsightBar';
// --- END UPDATE

const InventoryFormModal = ({ isOpen, onClose, item, onSave }) => {
  const [formData, setFormData] = useState({
    sku: '', name: '', description: '', quantity: 0,
    low_stock_threshold: 10, reorder_quantity: 10, unit_price: 0.0,
    category_id: '', supplier_id: '', warehouse_location: '', status: 'active', reason: '', keywords: ''
  });

  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAIGenerating, setIsAIGenerating] = useState(false);
  // --- UPDATE
  const [insight, setInsight] = useState(null);
  const [insightState, setInsightState] = useState('idle'); // idle | loading | ready | error
  const [externalChange, setExternalChange] = useState(null);
  // --- END UPDATE

  const toast = useToast();
  const modalBg = useColorModeValue("white", "gray.800");
  const sectionBg = useColorModeValue("gray.50", "gray.750");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const aiBg = useColorModeValue("teal.50", "whiteAlpha.100"); // --- UPDATE
  const mutedText = useColorModeValue("gray.600", "gray.400"); // --- UPDATE

  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const [suppliersRes, categoriesRes] = await Promise.all([
          supplierService.getAllSuppliers(),
          inventoryCategoryService.getAllCategories(),
        ]);
        setSuppliers(Array.isArray(suppliersRes) ? suppliersRes : suppliersRes?.data || []); // --- UPDATE: { data } responses too
        setCategories(Array.isArray(categoriesRes) ? categoriesRes : categoriesRes?.data || []); // --- UPDATE
      } catch (error) {
        toast({ title: 'Error fetching data.', status: 'error' });
      }
    };
    if (isOpen) {
      fetchDropdownData();
      setExternalChange(null); // --- UPDATE
      if (item) {
        setFormData({
          ...item,
          reason: '',
          sku: item.sku || '',
          warehouse_location: item.warehouse_location || '',
          reorder_quantity: item.reorder_quantity || 10,
          status: item.status || 'active',
          keywords: item.keywords || ''
        });
      } else {
        setFormData({
          sku: '', name: '', description: '', quantity: 0,
          low_stock_threshold: 10, reorder_quantity: 10, unit_price: 0.0,
          category_id: '', supplier_id: '', warehouse_location: '', status: 'active', reason: '', keywords: ''
        });
      }
    }
  }, [item, isOpen, toast]);

  // --- UPDATE (MOW+ v2): FlowSense ML insight for an existing item
  const loadInsight = React.useCallback(async () => {
    if (!item?.id) { setInsight(null); setInsightState('idle'); return; }
    setInsightState('loading');
    try { const r = await inventoryIntelApi.insight(item.id); setInsight(r); setInsightState('ready'); } catch (_) { setInsight(null); setInsightState('error'); }
  }, [item]);
  useEffect(() => { if (isOpen) loadInsight(); }, [isOpen, loadInsight]);

  // live: someone else changed this item while the form is open
  useInventoryLive(async (evt) => {
    if (!item?.id || evt.type === 'poll') return;
    try {
      const fresh = await inventoryService.getItemById(item.id);
      const f = fresh?.data || fresh;
      if (f && Number(f.quantity) !== Number(item.quantity)) setExternalChange({ quantity: Number(f.quantity), at: new Date() });
      loadInsight();
    } catch (_) { /* item may have been deleted */ }
  }, { enabled: !!(isOpen && item?.id), pollMs: 0, filter: (e) => e.item_id == null || String(e.item_id) === String(item?.id) });

  const aiHints = React.useMemo(() => itemFormHints(insight, formData), [insight, formData]);
  const applyAi = () => {
    setFormData((prev) => ({ ...prev, ...aiHints.suggested, reason: prev.reason || 'FlowSense AI re-order values' }));
    toast({ title: 'FlowSense values applied', description: 'Review and save the item.', status: 'info', duration: 2500 });
  };
  const ai = insight?.item || null;
  // --- END UPDATE

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleNumberChange = (value, name) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // --- AI Auto-Cataloging Handler ---
  const handleAIGenerate = async () => {
    if (!formData.name) {
      toast({ title: 'Item name required', description: 'Please enter a name first.', status: 'warning' });
      return;
    }
    setIsAIGenerating(true);
    try {
      const categoryName = categories.find(c => c.id === Number(formData.category_id))?.name || 'General Water Infrastructure';

      const result = await inventoryService.generateItemDetails(formData.name, categoryName);

      if (result.success && result.data) {
        setFormData(prev => ({
          ...prev,
          description: result.data.description || prev.description,
          keywords: result.data.keywords || prev.keywords
        }));
        toast({ title: 'AI Details Generated ✨', status: 'success', duration: 2000 });
      }
    } catch (error) {
      toast({
        title: 'AI Generation Failed',
        description: 'Ensure the local AI engine (Ollama) is running.',
        status: 'error'
      });
    } finally {
      setIsAIGenerating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const payload = {
      ...formData,
      category_id: formData.category_id || null,
      supplier_id: formData.supplier_id || null,
    };
    try {
      if (item) {
        await inventoryService.updateItem(item.id, payload);
      } else {
        await inventoryService.createItem(payload);
      }
      toast({ title: `Item ${item ? 'updated' : 'created'}.`, status: 'success' });
      onSave();
      onClose();
    } catch (error) {
      toast({ title: 'An error occurred.', description: error?.response?.data?.message || error?.message, status: 'error' }); // --- UPDATE: server message
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
      <ModalOverlay backdropFilter="blur(8px)" />
      <ModalContent as="form" onSubmit={handleSubmit} bg={modalBg} borderRadius="2xl">
        <Box h="4px" bgGradient="linear(to-r, blue.500, teal.400)" borderTopRadius="2xl" />
        <ModalHeader>
          <HStack>
            <Icon as={MdInventory} color="blue.500" />
            <Text>{item ? 'Edit Inventory Item' : 'Add New Item'}</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody p={6}>
          {/* --- UPDATE (MOW+ v2): live change banner + FlowSense AI */}
          {externalChange && (
            <Alert status="warning" borderRadius="xl" mb={4}><AlertIcon />
              <Box flex={1}><Text fontWeight="700">This item was changed by someone else while you were editing.</Text>
                <Text fontSize="sm">Quantity is now {externalChange.quantity} (you opened it at {item?.quantity}). Saving will overwrite their quantity with the value in this form.</Text></Box>
              <Button size="sm" ml={2} onClick={() => { setFormData((p) => ({ ...p, quantity: externalChange.quantity })); setExternalChange(null); }}>Use {externalChange.quantity}</Button>
            </Alert>
          )}
          <Box mb={5}>
            <FlowSenseInsightBar screen="item_form" compact data={null} rulesAnswer={() => [explainScreen('item_form', null, { hints: aiHints.hints })].join('\n')} extra={{ hints: aiHints.hints, item: ai ? { name: ai.name, quantity: ai.quantity, reorder_point: ai.reorder_point, eoq: ai.eoq, days_of_cover: ai.days_of_cover, risk: ai.risk } : null }}
              headline={ai ? [`${ai.name}: ${ai.quantity} in stock · ${ai.days_of_cover != null ? `${ai.days_of_cover} days of cover` : 'no recent usage'} · ${RISK_META[ai.risk]?.label || ai.risk}`, `ML re-order point ${dash(ai.reorder_point)} · safety stock ${dash(ai.safety_stock)} · EOQ ${dash(ai.eoq)} · usage ${dash(ai.avg_daily_usage)}/day`]
                : item ? (insightState === 'loading' ? 'Loading FlowSense insight…' : 'FlowSense insight unavailable for this item.') : 'New item — FlowSense starts forecasting once stock is issued.'} />
            {(insightState === 'loading') && <Skeleton h="40px" mt={2} borderRadius="lg" />}
            {aiHints.hints.length > 0 && (
              <Box bg={aiBg} borderRadius="xl" p={3} mt={2}>
                <VStack align="stretch" spacing={1}>
                  {aiHints.hints.map((h, i) => (
                    <HStack key={i} align="start" spacing={2}><Badge colorScheme={h.level === 'error' ? 'red' : h.level === 'warning' ? 'orange' : 'teal'} fontSize="2xs" mt={0.5}>{h.level === 'error' ? 'risk' : h.level === 'warning' ? 'check' : 'info'}</Badge><Text fontSize="sm">{h.text}</Text></HStack>
                  ))}
                </VStack>
                {Object.keys(aiHints.suggested).length > 0 && (
                  <Button mt={2} size="sm" colorScheme="teal" leftIcon={<Icon as={MdCheckCircle} />} onClick={applyAi}>
                    Use FlowSense values ({Object.entries(aiHints.suggested).map(([k, v]) => `${k === 'low_stock_threshold' ? 'threshold' : 're-order qty'} ${v}`).join(', ')})
                  </Button>
                )}
                {ai?.suggested_order_qty > 0 && <Text fontSize="xs" color={mutedText} mt={1}>FlowSense suggests ordering {ai.suggested_order_qty} now ({money(ai.suggested_order_value)}).</Text>}
              </Box>
            )}
          </Box>
          {/* --- END UPDATE */}
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>

            {/* Left Column: Basic Details & AI */}
            <VStack spacing={5} align="stretch" bg={sectionBg} p={5} borderRadius="xl" borderWidth="1px" borderColor={borderColor}>
              <Text fontWeight="bold" color="gray.500" fontSize="sm" textTransform="uppercase">Core Identity</Text>

              <FormControl isRequired>
                <FormLabel>Item Name</FormLabel>
                <InputGroup>
                    <Input
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        bg={modalBg}
                        placeholder="e.g. 4-inch PVC Valve"
                        maxLength={150} // --- UPDATE
                    />
                    <InputRightElement>
                        <Tooltip label="FlowSense AI: Auto-fill description">
                            <IconButton
                                size="sm"
                                variant="ghost"
                                colorScheme="purple"
                                icon={isAIGenerating ? <Spinner size="xs" /> : <MdAutoAwesome />}
                                onClick={handleAIGenerate}
                                isDisabled={isAIGenerating}
                                aria-label="FlowSense AI: auto-fill description" // --- UPDATE
                            />
                        </Tooltip>
                    </InputRightElement>
                </InputGroup>
              </FormControl>

              <FormControl>
                <Flex justify="space-between" align="center" mb={2}>
                  <FormLabel mb={0}>Description & Tags</FormLabel>
                  <Tooltip label="Auto-write description and keywords using AI">
                    <Button
                      size="xs"
                      colorScheme="purple"
                      variant="outline" // --- UPDATE: "subtle" is not a Button variant
                      leftIcon={<Icon as={FaRobot} />}
                      onClick={handleAIGenerate}
                      isLoading={isAIGenerating}
                      loadingText="Thinking..."
                      borderRadius="full"
                    >
                      ✨ AI Catalog
                    </Button>
                  </Tooltip>
                </Flex>
                <Textarea name="description" value={formData.description || ''} onChange={handleChange} bg={modalBg} rows={3} mb={3} />
                <Input name="keywords" value={formData.keywords || ''} onChange={handleChange} bg={modalBg} placeholder="Keywords (comma separated)" fontSize="sm" />
              </FormControl>

              <SimpleGrid columns={2} spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Category</FormLabel>
                  <Select name="category_id" value={formData.category_id || ''} onChange={handleChange} bg={modalBg}>
                    <option value="">Select...</option>
                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel>SKU / Barcode</FormLabel>
                  <Input name="sku" value={formData.sku} onChange={handleChange} bg={modalBg} placeholder="Auto-generated if empty" />
                </FormControl>
              </SimpleGrid>
            </VStack>

            {/* Right Column: Logistics & Financials */}
            <VStack spacing={5} align="stretch" bg={sectionBg} p={5} borderRadius="xl" borderWidth="1px" borderColor={borderColor}>
              <Text fontWeight="bold" color="gray.500" fontSize="sm" textTransform="uppercase">Warehouse Logistics</Text>

              <SimpleGrid columns={2} spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Current Qty</FormLabel>
                  <NumberInput value={formData.quantity} onChange={(val) => handleNumberChange(val, 'quantity')} min={0}>
                    <NumberInputField bg={modalBg} aria-label="Current quantity" />
                  </NumberInput>
                  {/* --- UPDATE */}
                  {item && <FormHelperText fontSize="xs">Tip: use Move stock to receive or issue — it records where the stock went.</FormHelperText>}
                </FormControl>
                <FormControl>
                  <FormLabel>Location</FormLabel>
                  <Input name="warehouse_location" value={formData.warehouse_location} onChange={handleChange} bg={modalBg} placeholder="e.g. Aisle 4, Bin B" />
                </FormControl>
              </SimpleGrid>

              <SimpleGrid columns={2} spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Alert Threshold</FormLabel>
                  <NumberInput value={formData.low_stock_threshold} onChange={(val) => handleNumberChange(val, 'low_stock_threshold')} min={0}>
                    <NumberInputField bg={modalBg} aria-label="Alert threshold" />
                  </NumberInput>
                  {ai?.reorder_point != null && <FormHelperText fontSize="xs" color="teal.500">FlowSense: {ai.reorder_point}</FormHelperText>}{/* --- UPDATE */}
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Reorder EOQ</FormLabel>
                  <NumberInput value={formData.reorder_quantity} onChange={(val) => handleNumberChange(val, 'reorder_quantity')} min={1}>
                    <NumberInputField bg={modalBg} aria-label="Reorder quantity" />
                  </NumberInput>
                  {ai?.eoq != null && <FormHelperText fontSize="xs" color="teal.500">FlowSense EOQ: {ai.eoq}</FormHelperText>}{/* --- UPDATE */}
                </FormControl>
              </SimpleGrid>

              <Divider borderColor={borderColor} />

              <Text fontWeight="bold" color="gray.500" fontSize="sm" textTransform="uppercase">Procurement</Text>

              <SimpleGrid columns={2} spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Unit Price ($)</FormLabel>
                  <NumberInput value={formData.unit_price} onChange={(val) => handleNumberChange(val, 'unit_price')} precision={2} step={0.01} min={0}>
                    <NumberInputField bg={modalBg} aria-label="Unit price" />
                  </NumberInput>
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Status</FormLabel>
                  <Select name="status" value={formData.status} onChange={handleChange} bg={modalBg}>
                    <option value="active">Active</option>
                    <option value="discontinued">Discontinued</option>
                    <option value="obsolete">Obsolete</option>
                  </Select>
                </FormControl>
              </SimpleGrid>

              <FormControl isRequired={suppliers.length > 0}>{/* --- UPDATE: required only when suppliers exist */}
                <FormLabel>Primary Supplier</FormLabel>
                <Select name="supplier_id" value={formData.supplier_id || ''} onChange={handleChange} bg={modalBg}>
                  <option value="">Select supplier...</option>
                  {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </Select>
              </FormControl>

              {item && (
                <FormControl>
                  <FormLabel>Reason for Edit</FormLabel>
                  <Input name="reason" value={formData.reason} onChange={handleChange} bg={modalBg} placeholder="e.g. Stock audit correction" />
                </FormControl>
              )}
            </VStack>
          </SimpleGrid>
        </ModalBody>

        <ModalFooter borderTopWidth="1px" borderColor={borderColor}>
          <Button variant="ghost" mr={3} onClick={onClose} borderRadius="full">Cancel</Button>
          <Button colorScheme="blue" type="submit" isLoading={isSubmitting} borderRadius="full" px={8}>
            Save Item
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

// Internal Helper for the AI Icon
// --- UPDATE: render the icon (Button has no `icon` prop, so the button was empty)
// --- UPDATE (Procurement MOW+ v1): forwardRef — the Tooltip around it needs the ref to position
const IconButton = React.forwardRef(({ icon, ...props }, ref) => (
    <Button
        ref={ref}
        {...props}
        padding={0}
        minW="32px"
        h="32px"
    >
      {icon}
    </Button>
));
IconButton.displayName = 'IconButton';
// --- END UPDATE

export default InventoryFormModal;
